import { createStyles, FormControl, FormControlLabel, IconButton, makeStyles, Radio, RadioGroup, Theme } from '@material-ui/core';
import React, { ElementRef, useEffect, useState } from 'react';
import { ChartCommonProps, DownIcon, IconButtonWrapper, UpIcon } from '../Common';
import { ChartFilter } from '../ChartComponents';
import { SearchCriteria } from '../SearchCriteria';
import CloseIcon from '@material-ui/icons/Close';
import { ETimePeriod, formatNumber, formatToDatePeriod } from '../../helpers/ChartHelper';
import gql from 'graphql-tag';
import { useQuery } from 'react-apollo';
import { DrillDownType } from '../DrillDownType';
import { SelectedItemProps } from '../MattersByStatusChart';
import VideoLibraryOutlinedIcon from '@material-ui/icons/VideoLibraryOutlined';
import MTooltip from '@material-ui/core/Tooltip';
import { VideoModal } from '../../components/VideoModal';
import { ChartInfo } from '../../components/ChartInfo';
// import ReactSpeedometer from "react-d3-speedometer"
import GaugeComponent from 'react-gauge-component';
import { GAUGE_PERIOD_LABELS, getBudgetProgressColor, RV_CHART_CLASS, rvGaugeArcStyle, rvGaugePointerStyle, rvGaugeTickLabelStyle, rvGaugeValueLabelStyle } from '../chartTheme';
import { ChartHeading } from '../ChartHeading';
import { ChartSkeleton } from '../../components/loading/ChartSkeleton';
import { ChartNotes } from '../ChartNotes';
import ReceiptOutlinedIcon from '@material-ui/icons/ReceiptOutlined';
import { AnimatedNumber } from '../AnimatedNumber';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            position: 'relative',
            paddingBottom: '10px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            maxWidth: '100%',
            maxHeight: '100%',
            height: '100%',
            textAlign: 'center',
            '@media print': {
                display: 'flex',
                flexFlow: 'column',
                alignItems: 'center',
                boxShadow: 'none !important',
            },
        },
        popover: {},
        paper: {},
        radioGroup: {
            flexFlow: 'row wrap',
            justifyContent: 'center',
            padding: '0 15px',
        },
        closeIcon: {
            color: 'rgb(0, 120, 212)',
        },
        titleContainer: {
            display: 'flex',
            flexFlow: 'column',
            padding: '20px 15px',
            '& .title': {
                fontSize: 16,
            },
            '& .colWrapper': {
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                paddingTop: 15,
                flexFlow: 'row wrap',
                margin: '0 -10px'
            },
            '& .col': {
                fontSize: '16px',
                fontWeight: 600,
                textAlign: 'center',
                border: '1px solid',
                padding: '15px',
                display: 'flex',
                margin: '0 10px 10px 10px',
                flexFlow: 'column',
                flex: 1,
                '& .value': {
                    fontSize: '36px',
                    padding: '20px 0',
                },
                '& .sub': {
                    display: 'flex',
                    flexFlow: 'row wrap',
                    justifyContent: 'center',
                    alignItems: 'center',
                },
                '& .status': {
                    marginLeft: '10px',
                    display: 'flex',
                    flexFlow: 'row wrap',
                    alignItems: 'center',
                },
            },
            '& .increase': {
                color: 'green',
            },
            '& .decrease': {
                color: 'red',
            },
        },
        barContainer: {
            padding: '5px 15px 15px 15px',
            minHeight: '350px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            maxHeight: '400px',
            maxWidth: 'auto',
            position: 'relative',
            '@media (max-width: 533px)': {
                maxWidth: '350px',
                margin: '0 auto',
            },
            '& .titleWrapper': {
                textAlign: 'center',
                display: 'inline-block',
                width: '100%',
                padding: '15px',
                '& .title': {
                    fontSize: '18px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '15px',
                },
                '& .value': {
                    fontSize: '24px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '15px',
                },
                '& .secondary-title': {
                    fontSize: '16px',
                    fontWeight: 400,
                    display: 'block',
                    marginBottom: '15px',
                },
            },
        },
        loadingWrapper: {
            padding: '20px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
        },
        title: {
            fontSize: 16,
            fontWeight: 600,
            fontFamily: 'inherit',
            marginBottom: 10,
        },
        span: {
            fontSize: '1rem',
        },
        totalDiv: {
            cursor: 'pointer',
            paddingRight: 5,
        },
        helpIcon: {
            position: 'absolute',
            left: '5px',
            top: '5px',
            display: 'flex',
            alignItems: 'center',
            margin: '0 -2.5px',
            '& > *': {
                margin: '0 2.5px',
            },
            '@media print': {
                display: 'none !important',
            },
        },
        learnIcon: {
            color: 'rgb(0, 120, 212)'
        },
        gaugeComponent: {
            width: '100%',
            maxHeight: '100%',
            maxWidth: '600px',
            margin: '0 auto',
        }
    }),
);

export const OptionsList = {
    MONTH: 'MONTH',
    PREVIOUS_MONTH: 'PREVIOUS_MONTH',
    FINANCIAL_YEAR: 'FINANCIAL_YEAR',
    PREV_FINANCIAL_YEAR: 'PREV_FINANCIAL_YEAR'
};
export type OptionsType = keyof typeof OptionsList;

interface FeesBilledVsBudgetSpeedometerChartProps extends SelectedItemProps, ChartCommonProps {
    chartFilter?: ChartFilter;
    infoUrl: string | null;
}

interface ChartState extends SpeedometerPeriod {
    options: OptionsType;
    symbol: string;
    leftPeriodString: string;
    rightPeriodString: string;

    currentFeesBilledYearTotal: number;
    previousFeesBilledYearTotal: number;
    budgetYearTotal: number;
    varianceYearTotal: number;
    percentageYearTotal: number;
}

export const FeesBilledVsBudgetSpeedometerChart: React.FC<FeesBilledVsBudgetSpeedometerChartProps> = (props) => {
    const classes = useStyles();

    const barContainerRef = React.useRef<ElementRef<'div'>>(null);

    const [isVideoModalOpen, setIsVideoModalOpen] = useState<boolean>(false);

    const feesBilledCumulativeVsBudget = useQuery<FeesBilledVsBudgetSpeedometerData>(FeesBilledVsBudgetSpeedometer, {
        variables: {
            entityType: props.chartFilter?.entityType,
            entityGuid: props.chartFilter?.entityGuid,
            responsibility: props.chartFilter?.responsibility,
        },
    });

    const [state, setState] = useState<ChartState>({
        options: 'MONTH',
        symbol: '$',
        leftPeriodString: formatToDatePeriod('monthToDate'),
        rightPeriodString: formatToDatePeriod('yearToDate'),
        budget: 0,
        currentFeesBilled: 0,
        percentage: 0,
        previousFeesBilled: 0,
        variance: 0,

        currentFeesBilledYearTotal: 0,
        previousFeesBilledYearTotal: 0,
        budgetYearTotal: 0,
        varianceYearTotal: 0,
        percentageYearTotal: 0,
    });

    const currencySymbolQuery = useQuery<CurrencySymbolData>(CURRENCY_SYMBOL_QUERY);

    useEffect(() => {
        if (currencySymbolQuery.data && currencySymbolQuery.data.options && !currencySymbolQuery.loading && !currencySymbolQuery.error) {
            setState((prevState) => {
                return {
                    ...prevState,
                    symbol: currencySymbolQuery.data!.options.currencySymbol.symbol,
                };
            });
        }
        // tslint:disable-next-line: align
    }, [currencySymbolQuery.loading, currencySymbolQuery.error, currencySymbolQuery.data]);

    useEffect(() => {
        setState((prevState) => {

            let leftPeriodString: string = '';
            let rightPeriodString: string = formatToDatePeriod('yearToDate');
            let budget = 0;
            let currentFeesBilled = 0;
            let percentage = 0;
            let previousFeesBilled = 0;
            let variance = 0;

            let currentFeesBilledYearTotal = 0;
            let previousFeesBilledYearTotal = 0;
            let budgetYearTotal = 0;
            let varianceYearTotal = 0;
            let percentageYearTotal = 0;

            if (!feesBilledCumulativeVsBudget.error && feesBilledCumulativeVsBudget.data) {

                const { financialYearToPreviousMonth, monthToDate, previousFinancialYear, previousMonth, previousMonthToDate } =
                    feesBilledCumulativeVsBudget.data.statistics.fees.feesBilledVsBudgetSpeedometer;

                currentFeesBilledYearTotal = financialYearToPreviousMonth ? financialYearToPreviousMonth.currentFeesBilled : 0;
                previousFeesBilledYearTotal = financialYearToPreviousMonth ? financialYearToPreviousMonth.previousFeesBilled : 0;
                budgetYearTotal = financialYearToPreviousMonth ? financialYearToPreviousMonth.budget : 0;
                varianceYearTotal = financialYearToPreviousMonth ? financialYearToPreviousMonth.variance : 0;
                percentageYearTotal = financialYearToPreviousMonth ? financialYearToPreviousMonth.percentage : 0;

                if (state.options === 'MONTH') {

                    budget = monthToDate ? monthToDate.budget : 0;
                    currentFeesBilled = monthToDate ? monthToDate.currentFeesBilled : 0;
                    percentage = monthToDate ? monthToDate.percentage : 0;
                    previousFeesBilled = monthToDate ? monthToDate.previousFeesBilled : 0;
                    variance = monthToDate ? monthToDate.variance : 0;
                    
                    leftPeriodString = formatToDatePeriod('monthToDate');

                } else if (state.options === 'PREVIOUS_MONTH') {

                    budget = previousMonth ? previousMonth.budget : 0;
                    currentFeesBilled = previousMonth ? previousMonth.currentFeesBilled : 0;
                    percentage = previousMonth ? previousMonth.percentage : 0;
                    previousFeesBilled = previousMonth ? previousMonth.previousFeesBilled : 0;
                    variance = previousMonth ? previousMonth.variance : 0;

                    currentFeesBilledYearTotal = previousMonthToDate ? previousMonthToDate.currentFeesBilled : 0;
                    previousFeesBilledYearTotal = previousMonthToDate ? previousMonthToDate.previousFeesBilled : 0;
                    budgetYearTotal = previousMonthToDate ? previousMonthToDate.budget : 0;
                    varianceYearTotal = previousMonthToDate ? previousMonthToDate.variance : 0;
                    percentageYearTotal = previousMonthToDate ? previousMonthToDate.percentage : 0;

                    leftPeriodString = formatToDatePeriod('previousMonth');
                    rightPeriodString = formatToDatePeriod('previousMonthYearToDate');

                } else if (state.options === 'FINANCIAL_YEAR') {

                    budget = financialYearToPreviousMonth ? financialYearToPreviousMonth.budget : 0;
                    currentFeesBilled = financialYearToPreviousMonth ? financialYearToPreviousMonth.currentFeesBilled : 0;
                    percentage = financialYearToPreviousMonth ? financialYearToPreviousMonth.percentage : 0;
                    previousFeesBilled = financialYearToPreviousMonth ? financialYearToPreviousMonth.previousFeesBilled : 0;
                    variance = financialYearToPreviousMonth ? financialYearToPreviousMonth.variance : 0;

                    leftPeriodString = formatToDatePeriod('yearToDate');

                } else {

                    budget = previousFinancialYear ? previousFinancialYear.budget : 0;
                    currentFeesBilled = previousFinancialYear ? previousFinancialYear.currentFeesBilled : 0;
                    percentage = previousFinancialYear ? previousFinancialYear.percentage : 0;
                    previousFeesBilled = previousFinancialYear ? previousFinancialYear.previousFeesBilled : 0;
                    variance = previousFinancialYear ? previousFinancialYear.variance : 0;

                    leftPeriodString = formatToDatePeriod('previousYearToDate');

                }

            }

            return {
                ...prevState,
                leftPeriodString,
                rightPeriodString,
                budget,
                currentFeesBilled,
                percentage,
                previousFeesBilled,
                variance,
                
                currentFeesBilledYearTotal,
                previousFeesBilledYearTotal,
                budgetYearTotal,
                varianceYearTotal,
                percentageYearTotal,
            };
        });
    // tslint:disable-next-line: align
    }, [state.options, feesBilledCumulativeVsBudget.loading, feesBilledCumulativeVsBudget.error, feesBilledCumulativeVsBudget.data]);

    const onToggleVideo = () => {
        setIsVideoModalOpen(!isVideoModalOpen);
    };

    const onCloseChart = () => {
        if (props.onCloseChart) {
            props.onCloseChart('isFeesBilledVsBudgetSpeedometerChartOpen');
        }
    };

    const onChangeRadio = (event: React.ChangeEvent<HTMLInputElement>) => {
        setState((prevState) => {
            return {
                ...prevState,
                // tslint:disable-next-line: radix
                options: (event.target as HTMLInputElement).value as OptionsType,
            };
        });
    };

    const handleTotalClick = (isMonth: Boolean) => {
        if (props.getSelectedItem) {
            const searchCriterias = new Array<SearchCriteria>();
            let defaultView = 'Fees Billed MTD/YTD';
            let timePeriod = ETimePeriod.THIS_MONTH;

            if (isMonth) {
                if (state.options === 'MONTH') {
                    searchCriterias.push({ property: 'Month', operator: '', comparison: '', period: 'curr' });
                    timePeriod = ETimePeriod.THIS_MONTH;
                } else if (state.options === 'PREVIOUS_MONTH') {
                    searchCriterias.push({ property: 'Month', operator: '', comparison: '', period: 'prev' });
                    defaultView = 'Fees Billed PM/PYTD';
                    timePeriod = ETimePeriod.PREVIOUS_MONTH;
                } else if (state.options === 'FINANCIAL_YEAR') {
                    searchCriterias.push({ property: 'FinancialYearToDate', operator: '', comparison: '', period: 'curr' });
                    timePeriod = ETimePeriod.FINANCIAL_YEAR;
                } else if (state.options === 'PREV_FINANCIAL_YEAR') {
                    searchCriterias.push({ property: 'FinancialYearToDate', operator: '', comparison: '', period: 'prev' });
                    defaultView = 'Fees Billed PM/PYTD';
                    timePeriod = ETimePeriod.PREVIOUS_FINANCIAL_YEAR;
                }
            } else {
                var period = 'curr';
                timePeriod = ETimePeriod.FINANCIAL_YEAR;
                if (state.options === 'PREVIOUS_MONTH') {
                    period = 'prevMonth';
                    defaultView = 'Fees Billed PM/PYTD';
                    timePeriod = ETimePeriod.PREVIOUS_MONTH;
                }

                searchCriterias.push({ property: 'FinancialYearToDate', operator: '', comparison: '', period: period });
            }

            const title = `Drilldown from Fees Billed vs Budget for ${timePeriod}`;

            props.getSelectedItem(searchCriterias, false, title, DrillDownType.staffBillingSummary, defaultView);
        }
    };

    const generateCustomSegmentStops = (maxValue: number, numSegments: number) => {
        if (numSegments <= 0) {
          throw new Error('Number of segments must be greater than 0');
        }

        if (maxValue <= 0) {
            throw new Error('maxValue must be greater than 0');
        }

        const stops: number[] = [];
        
        stops.push(0);
        
        const step = maxValue / numSegments;
        for (let i = 1; i < numSegments; i++) {
            const value = i * step;
            const roundedValue = Math.max(1, Math.round(value));
            if (roundedValue > stops[stops.length - 1]) {
                stops.push(roundedValue);
            } else {
                stops.push(stops[stops.length - 1] + 1);
            }
        }
        
        stops.push(maxValue);
        
        for (let i = 1; i < stops.length; i++) {
            if (stops[i] <= stops[i - 1]) {
                stops[i] = Math.min(stops[i - 1] + 1, maxValue);
            }
            if (stops[i] > maxValue) {
                stops[i] = maxValue;
            }
        }
        
        stops[stops.length - 1] = maxValue;
        
        return stops;
    };
      
    if (currencySymbolQuery.loading || feesBilledCumulativeVsBudget.loading) {
        return (
            <ChartSkeleton variant="gauge" />
        );
    }

    // const getMaxValues = state.budget > state.currentFeesBilled ? state.budget : state.currentFeesBilled;

    const safeBudget = Math.max(state.budget, 10);
    const customSegmentStops = generateCustomSegmentStops(safeBudget, 6);

    return (
        <div className={`${classes.root} ${RV_CHART_CLASS}`}>
            {isVideoModalOpen && props.infoUrl && <VideoModal open={isVideoModalOpen} onClose={onToggleVideo} url={props.infoUrl} />}
            <IconButtonWrapper aria-label="close">
                <div className={`${classes.helpIcon} rv-chart-help`}>
                    <ChartInfo
                        description={props.description}
                        fontSize={'medium'}
                        // className={classes.helpIcon}
                    />
                    {props.infoUrl && (
                        <MTooltip title="Learn more">
                            <IconButton className={classes.learnIcon} aria-label="maximize" onClick={onToggleVideo} size={props.showAllRecords ? 'medium' : 'small'}>
                                <VideoLibraryOutlinedIcon />
                            </IconButton>
                        </MTooltip>
                    )}
                </div>
                <IconButton aria-label="close" onClick={onCloseChart} size="small">
                    <CloseIcon className={classes.closeIcon} />
                </IconButton>
            </IconButtonWrapper>
            <div className={classes.titleContainer}>
                <ChartHeading icon={<ReceiptOutlinedIcon />} title="Fees Billed vs Budget" subtitle={GAUGE_PERIOD_LABELS[state.options]} />
                <div className="colWrapper">
                    <div className="col">
                        <div className="title">{state.leftPeriodString}</div>
                        <div className={`${classes.totalDiv} value`} onClick={() => handleTotalClick(true)}>
                            <AnimatedNumber value={state.currentFeesBilled} format={(amount) => `${state.symbol}${formatNumber(amount)}`} />
                        </div>
                        <div className="rv-meter">
                            <i style={{ width: `${Math.min(100, Math.max(0, state.percentage))}%`, backgroundColor: getBudgetProgressColor(state.percentage) }} />
                        </div>
                        <div className={`${classes.totalDiv} rv-budget`} onClick={() => handleTotalClick(true)}>
                            {`Budget ${state.symbol}${formatNumber(state.budget)}`}
                        </div>
                        <div className="rv-stats">
                            <div className={`${classes.totalDiv} rv-pct`} onClick={() => handleTotalClick(true)}>
                                {`${formatNumber(state.percentage)}% achvd`}
                            </div>
                            <div className={`rv-variance ${state.budget < state.currentFeesBilled ? 'good' : 'bad'}`} onClick={() => handleTotalClick(true)} title="Variance">
                                {state.budget < state.currentFeesBilled ? <UpIcon className="increase" /> : <DownIcon className="decrease" />}
                                {`${state.symbol}${formatNumber(state.variance)}`}
                            </div>
                        </div>
                    </div>
                    <div className="col">
                        <div className="title">{state.rightPeriodString}</div>
                        <div className={`${classes.totalDiv} value`} onClick={() => handleTotalClick(false)}>
                            <AnimatedNumber value={state.currentFeesBilledYearTotal} format={(amount) => `${state.symbol}${formatNumber(amount)}`} />
                        </div>
                        <div className="rv-meter">
                            <i style={{ width: `${Math.min(100, Math.max(0, state.percentageYearTotal))}%`, backgroundColor: getBudgetProgressColor(state.percentageYearTotal) }} />
                        </div>
                        <div className={`${classes.totalDiv} rv-budget`} onClick={() => handleTotalClick(false)}>
                            {`Budget ${state.symbol}${formatNumber(state.budgetYearTotal)}`}
                        </div>
                        <div className="rv-stats">
                            <div className={`${classes.totalDiv} rv-pct`} onClick={() => handleTotalClick(false)}>
                                {`${formatNumber(state.percentageYearTotal)}% achvd`}
                            </div>
                            <div className={`rv-variance ${state.budgetYearTotal < state.currentFeesBilledYearTotal ? 'good' : 'bad'}`} title="Variance">
                                {state.budgetYearTotal < state.currentFeesBilledYearTotal ? <UpIcon className="increase" /> : <DownIcon className="decrease" />}
                                {`${state.symbol}${formatNumber(state.varianceYearTotal)}`}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className={classes.barContainer} ref={barContainerRef}>
                <GaugeComponent
                    className={classes.gaugeComponent}
                    // key={state.options}
                    type="semicircle"
                    arc={{
                        ...rvGaugeArcStyle,
                        // gradient: true,
                        subArcs: [
                            {
                                limit: customSegmentStops[0],
                                color: '#f6414d',
                                showTick: true,
                                // tooltip: {
                                //     text: 'Too low temperature!',
                                // },
                                // onClick: () => console.log('AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA'),
                            },
                            {
                                limit: customSegmentStops[1],
                                color: '#f6414d',
                                showTick: true,
                                // tooltip: {
                                //     text: 'Low temperature!',
                                // },
                            },
                            {
                                limit: customSegmentStops[2],
                                color: '#f6414d',
                                showTick: true,
                                // tooltip: {
                                //     text: 'OK temperature!',
                                // },
                            },
                            {
                                limit: customSegmentStops[3],
                                color: '#f9a300',
                                showTick: true,
                                // tooltip: {
                                //     text: 'High temperature!',
                                // },
                            },
                            {
                                limit: customSegmentStops[4],
                                color: '#f9a300',
                                showTick: true,
                                // tooltip: {
                                //     text: 'High temperature!',
                                // },
                            },
                            {
                                limit: customSegmentStops[5],
                                color: '#87bb00',
                                showTick: true,
                                // tooltip: {
                                //     text: 'High temperature!',
                                // },
                            },
                            {
                                limit: customSegmentStops[6],
                                color: '#87bb00',
                                showTick: true,
                                // tooltip: {
                                //     text: 'High temperature!',
                                // },
                            },
                        ],
                    }}
                    pointer={rvGaugePointerStyle}
                    labels={{
                        valueLabel: {
                            hide: true,
                            formatTextValue: (value) => `${state.symbol}${formatNumber(state.currentFeesBilled > state.budget ? Math.round(state.currentFeesBilled) : value)}`,
                            style: rvGaugeValueLabelStyle
                        },
                        tickLabels: {
                            type: 'inner',
                            ticks: [
                                // { value: customSegmentStops[0] }, 
                                // { value: customSegmentStops[1] }, 
                                // { value: customSegmentStops[2] }, 
                                // { value: state.budget > state.currentFeesBilled ? state.budget : state.currentFeesBilled }
                            ],
                            defaultTickValueConfig: {
                                formatTextValue: (value) => `${state.symbol}${formatNumber(value)}`,
                                style: rvGaugeTickLabelStyle
                            },
                        },
                    }}
                    value={Math.round(state.currentFeesBilled > state.budget ? customSegmentStops[6] : state.currentFeesBilled)}
                    minValue={customSegmentStops[0]}
                    maxValue={customSegmentStops[6]}
                />
                <div className="rv-gauge-readout">
                    <div className="rv-gauge-value">
                        <AnimatedNumber value={state.currentFeesBilled} format={(amount) => `${state.symbol}${formatNumber(amount)}`} />
                    </div>
                    <div className="rv-gauge-caption">billed</div>
                </div>
                {/* {barContainerRef.current?.offsetWidth && (
                    <ReactSpeedometer 
                        // key={state.options}
                        valueFormat={`${state.symbol},`}
                        fluidWidth={true}
                        forceRender={true}
                        value={Math.round(state.currentFeesBilled)}
                        maxValue={customSegmentStops[4]}
                        minValue={customSegmentStops[0]}
                        customSegmentStops={customSegmentStops}
                        segmentColors={['#f6414d', '#f9a300', '#f9a300' ,'#87bb00']}
                        // width={barContainerRef.current.offsetWidth}
                    />
                )} */}
            </div>
            <FormControl component="div">
                <RadioGroup name="last" value={state.options} onChange={onChangeRadio} className={classes.radioGroup}>
                    <FormControlLabel value={OptionsList.MONTH} control={<Radio color="primary" />} label="This Month" />
                    <FormControlLabel value={OptionsList.PREVIOUS_MONTH} control={<Radio color="primary" />} label="Previous Month" />
                    <FormControlLabel value={OptionsList.FINANCIAL_YEAR} control={<Radio color="primary" />} label="Financial Year" />
                    <FormControlLabel value={OptionsList.PREV_FINANCIAL_YEAR} control={<Radio color="primary" />} label="Previous Financial Year" />
                </RadioGroup>
            </FormControl>
            <ChartNotes notes={['Information collated based on Timekeeper']} actionHint="Drill down to timekeeper details" />
        </div>
    );
};

export interface CurrencySymbolData {
    options: Options;
}

export interface Options {
    currencySymbol: CurrencySymbol;
}

export interface CurrencySymbol {
    symbol: string;
}

export const CURRENCY_SYMBOL_QUERY = gql`
    query CurrencySymbol {
        options {
            currencySymbol {
                symbol
            }
        }
    }
`;

export interface FeesBilledVsBudgetSpeedometerParams {
    entityType: number | null;
    entityGuid: string | null;
    responsibility: number | null;
}

export type FeesBilledVsBudgetSpeedometerData = {
    statistics: Statistics;
};

export type Statistics = {
    fees: Fees;
};

export type Fees = {
    feesBilledVsBudgetSpeedometer: FeesBilledVsBudgetSpeedometer;
};

export type FeesBilledVsBudgetSpeedometer = {
    monthToDate:                  SpeedometerPeriod | null;
    previousMonth:                SpeedometerPeriod | null;
    financialYearToPreviousMonth: SpeedometerPeriod | null;
    previousFinancialYear:        SpeedometerPeriod | null;
    previousMonthToDate:          SpeedometerPeriod | null;
};

export type SpeedometerPeriod = {
    currentFeesBilled:  number;
    previousFeesBilled: number;
    budget:             number;
    percentage:         number;
    variance:           number;
};

const FeesBilledVsBudgetSpeedometer = gql`
    query FeesBilledVsBudgetSpeedometer($entityType: Int, $entityGuid: String, $responsibility: Int) {
        statistics {
            fees {
                feesBilledVsBudgetSpeedometer(entityType: $entityType, entityGuid: $entityGuid, responsibility: $responsibility) {
                    monthToDate {
                        currentFeesBilled
                        previousFeesBilled
                        budget
                        percentage
                        variance
                    }
                    previousMonth {
                        currentFeesBilled
                        previousFeesBilled
                        budget
                        percentage
                        variance
                    }
                    previousMonthToDate {
                        currentFeesBilled
                        previousFeesBilled
                        budget
                        percentage
                        variance
                    }
                    financialYearToPreviousMonth {
                        currentFeesBilled
                        previousFeesBilled
                        budget
                        percentage
                        variance
                    }
                    previousFinancialYear {
                        currentFeesBilled
                        previousFeesBilled
                        budget
                        percentage
                        variance
                    }
                }
            }
        }
    }
`;
